import React, { useState, useContext } from 'react';
import { Plus, X, Save, ArrowLeft, Calendar, AlertTriangle, User, ChevronDown } from 'lucide-react';


import AuthContext from '../../../../context/AuthContext';
import riskService from '../../../../services/riskService';
import { useToast } from '../../../../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import userService from '../../../../services/userService';

export default function AddRisk() {
  const navigator = useNavigate()
  const { user, authTokens } = useContext(AuthContext);
  const { showError, showSuccess } = useToast();
  
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    probabilite: '',
    riskDate: '',
    consequence: ''
  });

  const [actions, setActions] = useState([{
    description: '',
    assignedUser: null,
    assignedUserDisplay: '',
    selectedRole: null,
    dueDate: ''
  }]);

  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [currentActionIndex, setCurrentActionIndex] = useState(null);

  const probabiliteOptions = [
    { value: 'mineur', label: 'Mineur', color: 'bg-blue-100 text-blue-800' },
    { value: 'normal', label: 'Normal', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'majeur', label: 'Majeur', color: 'bg-orange-100 text-orange-800' },
    { value: 'critique', label: 'Critique', color: 'bg-red-100 text-red-800' }
  ];

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleActionChange = (index, field, value) => {
    const newActions = [...actions];
    newActions[index] = { ...newActions[index], [field]: value };
    setActions(newActions);
  };

  const addAction = () => {
    setActions([...actions, { 
      description: '', 
      assignedUser: null,
      assignedUserDisplay: '',
      selectedRole: null,
      dueDate: '' 
    }]);
  };

  const removeAction = (index) => {
    if (actions.length > 1) {
      setActions(actions.filter((_, i) => i !== index));
    }
  };

  const searchUsers = async (query, actionIndex) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      setShowUserDropdown(false);
      return;
    }

    try {
      const result = await userService.filterUsersBySearch(query, authTokens.access);
      if (result.success) {
        setSearchResults(result.data);
        setShowUserDropdown(true);
        setCurrentActionIndex(actionIndex);
      }
    } catch (error) {
      console.error('Error searching users:', error);
      setSearchResults([]);
      setShowUserDropdown(false);
    }
  };

  const handleUserSearchInput = (query, actionIndex) => {
    const newActions = [...actions];
    newActions[actionIndex] = { 
      ...newActions[actionIndex], 
      assignedUserDisplay: query,
      assignedUser: null,
      selectedRole: null
    };
    setActions(newActions);
    
    setUserSearchQuery(query);
    searchUsers(query, actionIndex);
  };

  const selectUser = (selectedUser, actionIndex) => {
    const userDisplay = `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`;
    
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      assignedUser: selectedUser,
      assignedUserDisplay: userDisplay,
      selectedRole: null
    };
    setActions(newActions);
    
    setUserSearchQuery('');
    setShowUserDropdown(false);
    setSearchResults([]);
    setCurrentActionIndex(null);
  };

  const handleRoleSelect = (actionIndex, role) => {
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      selectedRole: role
    };
    setActions(newActions);
  };

  const clearUserSelection = (actionIndex) => {
    const newActions = [...actions];
    newActions[actionIndex] = {
      ...newActions[actionIndex],
      assignedUser: null,
      assignedUserDisplay: '',
      selectedRole: null
    };
    setActions(newActions);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate actions with users must have roles selected
    const invalidActions = actions.filter(action => 
      action.description.trim() !== '' && 
      !action.assignedUser 
    );

    if (invalidActions.length > 0) {
      showError("Veuillez sélectionner un rôle pour tous les utilisateurs assignés.");
      return;
    }

    const validActions = actions.filter(action => action.description.trim() !== '');

    const processedActions = validActions.map(action => ({
      action: action.description,
      assignedUserId: action.assignedUser ? action.assignedUser.id : null,
      userRoleAssignedId: action.selectedRole ? action.selectedRole.id : null,
      dueDate: action.dueDate || null,
    }));

    const backendData = {
      risk_name: formData.nom,
      description: formData.description,
      probability: formData.probabilite,
      risk_date: formData.riskDate,
      status: "pending",
      consequence: formData.consequence,
      actions: processedActions,
    };

    console.log('Sending to backend:', backendData);

    try {
      const result = await riskService.createRisk(backendData, authTokens.access);

      if (result.success) {
        showSuccess("Risque créé avec succès !");
        setFormData({
          nom: '',
          description: '',
          probabilite: '',
          riskDate: '',
          consequence: ''
        });
        setActions([{ 
          description: '', 
          assignedUser: null, 
          assignedUserDisplay: '', 
          selectedRole: null,
          dueDate: '' 
        }]);
      } else {
        console.error('Backend error:', result.errorData);
        showError(result.error || "Erreur lors de la création du risque");
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      showError("Erreur inattendue. Veuillez réessayer.");
    }
  };

  const handleCancel = () => {
    navigator(-1)
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3">
        <button 
          onClick={handleCancel} 
          className="p-2 text-gray-500 hover:text-gray-700 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h2 className="text-xl font-semibold text-gray-900">
          Ajouter un nouveau risque
        </h2>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white border rounded-lg shadow-sm p-6 space-y-6">
        
        {/* Risk Name and Date */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom du risque <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              placeholder="Entrer le nom du risque..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 inline mr-2" />
              Date du risque <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.riskDate}
              onChange={(e) => handleInputChange('riskDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>
        </div>

        {/* Consequence */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <AlertTriangle className="w-4 h-4 inline mr-2" />
            Conséquence <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.consequence}
            onChange={(e) => handleInputChange('consequence', e.target.value)}
            placeholder="Décrire la conséquence du risque..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Décrire le risque en détail..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500"
            required
          />
        </div>

        {/* Probability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Probabilité <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {probabiliteOptions.map(option => (
              <label
                key={option.value}
                className={`flex items-center justify-center p-3 border rounded-lg cursor-pointer ${
                  formData.probabilite === option.value
                    ? 'border-teal-500 bg-teal-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  type="radio"
                  name="probabilite"
                  value={option.value}
                  checked={formData.probabilite === option.value}
                  onChange={(e) => handleInputChange('probabilite', e.target.value)}
                  className="sr-only"
                />
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${option.color}`}>
                  {option.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium text-gray-700">
              Actions préventives/correctives
            </label>
            <button
              type="button"
              onClick={addAction}
              className="flex items-center px-3 py-1 text-sm bg-teal-100 text-teal-700 rounded-md hover:bg-teal-200"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter une action
            </button>
          </div>

          <div className="space-y-4">
            {actions.map((action, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Action {index + 1}</h4>
                  {actions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeAction(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded-md"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {/* Action Description */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Description de l'action
                    </label>
                    <textarea
                      value={action.description}
                      onChange={(e) => handleActionChange(index, 'description', e.target.value)}
                      placeholder={`Décrire l'action ${index + 1}...`}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* User Assignment */}
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <User className="w-3 h-3 inline mr-1" />
                        Utilisateur assigné
                      </label>
                      <input
                        type="text"
                        value={action.assignedUserDisplay || ''}
                        onChange={(e) => handleUserSearchInput(e.target.value, index)}
                        placeholder="Rechercher un utilisateur (min 2 caractères)..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                      />

                      {/* User Search Dropdown */}
                      {showUserDropdown && currentActionIndex === index && searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-40 overflow-y-auto">
                          {searchResults.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => selectUser(user, index)}
                              className="w-full px-3 py-2 text-left hover:bg-gray-50 text-sm"
                            >
                              <div className="font-medium">
                                {user.first_name} {user.last_name} <span className="text-gray-500 text-xs">{user.role_function}</span>
                              </div>
                              <div className="text-gray-500 text-xs">
                                {user.email}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Due Date */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Date d'échéance
                      </label>
                      <input
                        type="date"
                        value={action.dueDate}
                        onChange={(e) => handleActionChange(index, 'dueDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Role Selection - Shows after user is selected */}
                  {action.assignedUser && (
                    <div className="mt-3 p-3 bg-white border border-gray-200 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-xs font-medium text-gray-700">
                          {/* Sélectionner le rôle */}
                          Rôle/position de l'utilisateur sélectionné: {action.assignedUser.role_function}
                        </label>
                        {/* <button
                          type="button"
                          onClick={() => clearUserSelection(index)}
                          className="text-xs text-red-600 hover:text-red-700"
                        >
                          Changer d'utilisateur
                        </button> */}
                      </div>

                      {/* {action.assignedUser.roles.length === 0 ? (
                        <div className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md">
                          ⚠️ Cet utilisateur n'a aucun rôle assigné. Veuillez sélectionner un autre utilisateur.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {action.assignedUser.roles.map((role) => (
                            <label
                              key={role.id}
                              className={`flex items-center p-2 border rounded-md cursor-pointer text-sm ${
                                action.selectedRole?.id === role.id
                                  ? 'border-teal-500 bg-teal-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="radio"
                                name={`role-${index}`}
                                checked={action.selectedRole?.id === role.id}
                                onChange={() => handleRoleSelect(index, role)}
                                className="mr-2"
                              />
                              <span className="font-medium">{role.role_name}</span>
                            </label>
                          ))}
                        </div>
                      )} */}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t">
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="flex items-center px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          >
            <Save className="h-4 w-4 mr-2" />
            Enregistrer le risque
          </button>
        </div>
      </form>
    </div>
  );
}